import Iconify from "@/components/ui/Iconify";
import {
  useAreaChips,
  AREA_CHIP_MAX_ZOOM,
} from "@/components/maplibre-map/use-area-chips";
import { useAreaLandmarks } from "@/components/maplibre-map/use-area-landmarks";
import { useSubwayNetwork } from "@/components/maplibre-map/use-subway-network";
import Image from "next/image";
import MapLibreMap from "@/components/maplibre-map";
import {
  getPlaceId,
  getPlaceLatLng,
} from "@/components/maplibre-map/places-adapter";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";
import {
  DEFAULT_PLACES_MAP_ZOOM,
  HOTPLE_DEFAULT_CENTER,
  MAPLIBRE_PM_TILES_URL,
} from "@/components/maplibre-map/config";
import { getMediaRecordUrl } from "@/utils/media";
import { useTranslations } from "@/lib/static-intl";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PlacesMapsDistanceFilterTabs from "../map-controls/places-maps-distance-filter-tabs";

const MAP_PIN_REFRESH_DEBOUNCE_MS = 75;
const HOTPLE_SELECTED_PLACE_ZOOM = DEFAULT_PLACES_MAP_ZOOM;

// Auto-refresh (left list) tuning — mirrors the mobile map behavior:
// 지도가 멈출 때마다 핀은 매번 다시 그린다. 목록도 그에 맞춰 다시 부른다 —
// 문턱이 높으면 핀과 목록이 어긋난다(가까이서 조금씩 움직이면 핀 8개, 목록 3개).
//
// 이동 350m → 120m: 가까이 볼수록 350m 는 화면 몇 개 분량이라 너무 컸다.
// 확대도 포함: "이미 받은 범위 안이니 불필요" 라는 전제가 틀렸다. 목록에는 개수 상한이
//   있어서, 넓게 받은 결과가 좁은 화면의 가게를 다 담고 있지 않다.
// 디바운스 150ms → 400ms: 문턱을 낮춘 만큼 요청이 늘므로, 손을 떼고 화면이 멎은 뒤에만 부른다.
const AREA_AUTO_REFRESH_MIN_PAN_METERS = 120;
const AREA_AUTO_REFRESH_MIN_ZOOM_CHANGE = 0.8;
const AREA_AUTO_REFRESH_DEBOUNCE_MS = 400;

const areaRefreshDistanceMeters = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const buildSearchResultMarker = ({
  mapRedPinCenter,
  placeId,
  placeSearchMapPinProperties,
}) => {
  if (!mapRedPinCenter?.lat || !mapRedPinCenter?.lng) return null;

  const sourceProperties =
    placeSearchMapPinProperties?.properties || placeSearchMapPinProperties || {};

  return {
    id: placeId,
    isSearchResultPin: true,
    geometry: {
      type: "Point",
      coordinates: [Number(mapRedPinCenter.lng), Number(mapRedPinCenter.lat)],
    },
    properties: {
      ...sourceProperties,
      id: placeId,
      name: sourceProperties?.name || sourceProperties?.address || "Place",
    },
  };
};

const PLACE_MARKER_ID_KEYS = [
  "_id",
  "id",
  "placeId",
  "placeUniqueNumber",
  "publicId",
  "contentid",
  "contentId",
];

const getPlaceMarkerIds = (place, fallbackId) =>
  Array.from(
    new Set(
      [
        fallbackId,
        ...PLACE_MARKER_ID_KEYS.map((key) => place?.[key]),
        ...(Array.isArray(place?.placeIds) ? place.placeIds : []),
        ...(Array.isArray(place?.properties?.placeIds)
          ? place.properties.placeIds
          : []),
      ]
        .filter((value) => value != null && value !== "")
        .map(String)
    )
  );

const hasPlaceMarkerId = (place, placeId) => {
  if (!placeId) return false;
  return getPlaceMarkerIds(place).some((id) => id === String(placeId));
};

const buildSelectedPlaceMarker = ({ place, placeId }) => {
  const markerCenter = getPlaceLatLng(place);
  if (!markerCenter || !placeId) return null;

  const placeIds = getPlaceMarkerIds(place, placeId);

  return {
    id: String(placeId),
    isSearchResultPin: true,
    placeIds,
    geometry: {
      type: "Point",
      coordinates: [Number(markerCenter.lng), Number(markerCenter.lat)],
    },
    properties: {
      ...(place?.properties || {}),
      id: String(placeId),
      isSearchResultPin: true,
      name: place?.name || place?.address || place?.properties?.name || "Place",
      placeIds,
      type: place?.type || place?.properties?.type,
    },
  };
};

const getInitialMapCenter = (mapCenter) => mapCenter || HOTPLE_DEFAULT_CENTER;

const getInitialMapZoom = (mapZoom) => mapZoom || DEFAULT_PLACES_MAP_ZOOM;

const isFiniteCoordinate = (coordinate) =>
  Number.isFinite(coordinate?.lat) && Number.isFinite(coordinate?.lng);

const isLngWithinBounds = (lng, west, east) => {
  if (west <= east) return lng >= west && lng <= east;
  return lng >= west || lng <= east;
};

const isCoordinateWithinBounds = (coordinate, bounds) => {
  if (!isFiniteCoordinate(coordinate) || !bounds) return false;

  const south = Number(bounds.south);
  const west = Number(bounds.west);
  const north = Number(bounds.north);
  const east = Number(bounds.east);

  if (
    !Number.isFinite(south) ||
    !Number.isFinite(west) ||
    !Number.isFinite(north) ||
    !Number.isFinite(east)
  ) {
    return false;
  }

  return (
    coordinate.lat >= south &&
    coordinate.lat <= north &&
    isLngWithinBounds(coordinate.lng, west, east)
  );
};

const toCount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const formatCount = (value) => new Intl.NumberFormat("ko-KR").format(value || 0);

const getReviewCount = (place) =>
  Math.max(
    toCount(place?.totalReviews),
    toCount(place?.totalDbReviews),
    toCount(place?.numberOfVisitorReviews),
    toCount(place?.numberOfBlogReviews)
  );

const formatRating = (value) => {
  const rating = Number(value || 0);
  if (!Number.isFinite(rating) || rating <= 0) return "";
  return rating.toFixed(1);
};

const getPopupRatingLabel = (place) => {
  const hotpleRating = Number(place?.hotpleAverageRating || 0);
  const externalRating = Number(
    place?.naverRating ?? place?.nRating ?? place?.averageRating ?? 0
  );
  const rating = Number.isFinite(hotpleRating) && hotpleRating > 0
    ? hotpleRating
    : externalRating;
  const ratingLabel = formatRating(rating);
  const reviewCount = getReviewCount(place);

  if (ratingLabel && reviewCount > 0) {
    return `평점 ${ratingLabel} · 리뷰 ${formatCount(reviewCount)}`;
  }
  if (ratingLabel) return `평점 ${ratingLabel}`;
  if (reviewCount > 0) return `리뷰 ${formatCount(reviewCount)}`;
  return "";
};

const getPopupAddress = (place) =>
  place?.address ||
  place?.old_address ||
  [place?.city, place?.state, place?.country].filter(Boolean).join(" ");

const getPopupImageSrc = (place) => {
  const media = Array.isArray(place?.placeMedia) ? place.placeMedia[0] : null;
  return media
    ? getMediaRecordUrl(media, {
        variant: "thumbnail",
        preferPlaceholder: true,
      })
    : "";
};

const CompactPlaceMapPopup = ({
  place,
  onClose,
  onOpenDetail,
}) => {
  if (!place) return null;

  const imageSrc = getPopupImageSrc(place);
  const ratingLabel = getPopupRatingLabel(place);
  const address = getPopupAddress(place);

  return (
    <aside
      className="pointer-events-auto relative z-30 w-[300px] rounded-xl border border-black/10 bg-white p-3 shadow-xl shadow-black/15 dark:border-white/10 dark:bg-custom_black"
      style={{ willChange: "transform" }}
    >
      <div className="absolute bottom-[-8px] left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-b border-r border-black/10 bg-white dark:border-white/10 dark:bg-custom_black" />
      <button
        aria-label="닫기"
        className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/85 text-black/55 hover:bg-black/5 dark:bg-custom_black/85 dark:text-white/60 dark:hover:bg-white/10"
        onClick={onClose}
        type="button"
      >
        <Iconify icon="mdi:close" className="!h-4 !w-4 !text-inherit" />
      </button>
      <div className="relative grid grid-cols-[57px_minmax(0,1fr)] gap-3">
        <div className="relative h-[76px] w-[57px] overflow-hidden rounded-lg border border-black/10 bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.04]">
          {imageSrc ? (
            <Image
              alt={place?.name || "place image"}
              className="object-cover"
              fill
              sizes="57px"
              src={imageSrc}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-black/25 dark:text-white/25">
              <Iconify icon="mdi:image-outline" className="!h-8 !w-8 !text-inherit" />
            </div>
          )}
        </div>
        <div className="min-w-0 pr-5">
          <p className="truncate text-base font-bold leading-tight tracking-normal text-black dark:text-white">
            {place?.name || "장소"}
          </p>
          {address ? (
            <p className="mt-1 line-clamp-1 text-xs leading-snug text-black/60 dark:text-white/60">
              {address}
            </p>
          ) : null}
          {ratingLabel ? (
            <p className="mt-1 text-xs font-semibold leading-snug text-black/70 dark:text-white/70">
              {ratingLabel}
            </p>
          ) : null}
          <div className="mt-3 flex items-center gap-2">
            <button
              className="inline-flex h-8 items-center justify-center rounded-md bg-red px-3 text-xs font-bold text-white hover:bg-red/90"
              onClick={onOpenDetail}
              type="button"
            >
              상세 보기
            </button>
            <button
              className="inline-flex h-8 items-center justify-center rounded-md border border-black/10 bg-white px-3 text-xs font-semibold text-black/70 hover:bg-black/[0.04] dark:border-white/10 dark:bg-transparent dark:text-white/70 dark:hover:bg-white/[0.06]"
              type="button"
            >
              길찾기
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

// Safe-rect padding for the "reveal only if clipped" pin-focus behavior, sized
// from CompactPlaceMapPopup's real geometry: w-[300px] card rendered ABOVE the
// pin with an ~24px overlay offset + a downward arrow, body ~130px tall. The pin
// sits at the bottom-center, so the card occupies pin.y-(24+~130)..pin.y and
// ±150px horizontally. top = cardHeight(~130) + ~40 margin; left/right =
// cardWidth/2 (150) + ~15; bottom = ~40 for the pin glyph below its anchor.
const COMPACT_POPUP_FOCUS_PADDING = {
  top: 170,
  right: 165,
  bottom: 40,
  left: 165,
};

const PlaceMapMaplibre = ({
  mapMarkers,
  mapCenter,
  mapZoom,
  mapBounds,
  mapRadius,
  setMapBounds,
  setMapCenter,
  setMapZoom,
  setMapRadius,
  searchQuery,
  completePlacesReset,
  onAreaAutoRefresh,
  refreshMapMarkersForBounds,
  onAreaChipsChange = null,
  placeListLoader,
  placeScrollLoader,
  mapRedPinCenter,
  mapCameraJumpKey,
  placeSearchMapPinProperties,
  userFollowing,
  setUserFollowing,
  handlePostCardClick,
  changeMapCenterAndZoomIn,
  addQueryParam,
  isLeftSectionExpanded,
  setIsLeftSectionExpanded,
  userLocation,
  selectedPlacePreview,
  openPlaceAddModal,
  onGoToCurrentLocation,
  isLocatingUser = false,
  disableCameraAnimation = false,
  scrollZoom = true,
  clusterMarkers = true,
}) => {
  const searchParams = useSearchParams();
  const viewPlaceId = searchParams.get("view-place");
  const viewPlaceIdInList = searchParams.get("view-place-in-list");
  const searchPlace = searchParams.get("search-place");
  const t = useTranslations("places.place_map");
  const { closePlaceView, routeToPlaceDetail } = usePlaceViewNavigation({
    place: selectedPlacePreview,
  });

  const [localMarkers, setLocalMarkers] = useState([]);
  const [localCenter, setLocalCenter] = useState(() =>
    getInitialMapCenter(mapCenter)
  );
  const [localZoom, setLocalZoom] = useState(() => getInitialMapZoom(mapZoom));
  const [localBounds, setLocalBounds] = useState(mapBounds);
  const [compactPopupPortalElement, setCompactPopupPortalElement] =
    useState(null);
  const mapPinRefreshTimerRef = useRef(null);
  const initialMapPinRefreshRef = useRef(false);
  const focusedViewPlaceIdRef = useRef(null);

  // Auto-refresh-on-move state (mirrors mobile). Kept in refs so onUserMoveEnd
  // stays stable and reads the latest callback without re-subscribing.
  const areaAutoRefreshTimerRef = useRef(null);
  const lastAreaFetchCenterRef = useRef(null);
  const lastAreaFetchZoomRef = useRef(null);
  const onAreaAutoRefreshRef = useRef(onAreaAutoRefresh);
  onAreaAutoRefreshRef.current = onAreaAutoRefresh;

  useEffect(
    () => () => {
      if (areaAutoRefreshTimerRef.current) {
        clearTimeout(areaAutoRefreshTimerRef.current);
      }
    },
    []
  );

  const selectedMarkerId = viewPlaceId || viewPlaceIdInList;
  const isLoadingPlaces = placeListLoader || placeScrollLoader;

  const searchResultMarker = useMemo(
    () =>
      buildSearchResultMarker({
        mapRedPinCenter,
        placeId: viewPlaceIdInList,
        placeSearchMapPinProperties,
      }),
    [mapRedPinCenter, placeSearchMapPinProperties, viewPlaceIdInList]
  );

  const selectedLocalMarker = useMemo(() => {
    if (!viewPlaceId) return null;
    return (
      localMarkers.find((marker) => hasPlaceMarkerId(marker, viewPlaceId)) ||
      null
    );
  }, [localMarkers, viewPlaceId]);

  const selectedPlaceMarker = useMemo(() => {
    if (!viewPlaceId) return null;

    if (selectedLocalMarker) {
      return buildSelectedPlaceMarker({
        place: selectedLocalMarker,
        placeId: viewPlaceId,
      });
    }

    const previewMarker = buildSelectedPlaceMarker({
      place: selectedPlacePreview,
      placeId: viewPlaceId,
    });
    if (previewMarker) return previewMarker;

    return null;
  }, [
    selectedLocalMarker,
    selectedPlacePreview,
    viewPlaceId,
  ]);

  const displayedMarkers = useMemo(() => {
    if (viewPlaceId && selectedPlaceMarker) {
      const hasSelectedLocalMarker = localMarkers.some((marker) =>
        hasPlaceMarkerId(marker, viewPlaceId)
      );
      return hasSelectedLocalMarker
        ? localMarkers
        : [...localMarkers, selectedPlaceMarker];
    }
    if (viewPlaceIdInList && searchResultMarker) return [searchResultMarker];
    return localMarkers;
  }, [
    localMarkers,
    searchResultMarker,
    selectedPlaceMarker,
    viewPlaceId,
    viewPlaceIdInList,
  ]);

  const selectedPlaceOverlayCenter = useMemo(() => {
    if (!viewPlaceId) return null;
    const localMarkerCenter = getPlaceLatLng(selectedLocalMarker);
    const previewCenter = hasPlaceMarkerId(selectedPlacePreview, viewPlaceId)
      ? getPlaceLatLng(selectedPlacePreview)
      : null;
    return (
      localMarkerCenter ||
      previewCenter ||
      getPlaceLatLng(selectedPlaceMarker)
    );
  }, [
    selectedLocalMarker,
    selectedPlaceMarker,
    selectedPlacePreview,
    viewPlaceId,
  ]);

  const selectedPlaceFocusCenter = viewPlaceId
    ? selectedPlaceOverlayCenter
    : null;

  // Latch the selected place's coordinates for the WHOLE selection so the tracked
  // location never flickers to null when the pin scrolls OFF-SCREEN or drops out
  // of the refreshed marker set while dragging. Without this, trackedLocation went
  // null → the shared map's focus ref reset → it re-centered on the pin the moment
  // its coords reappeared ("drag the pin off screen and the map snaps back to
  // center it"). Latching keeps trackedLocation stable (same coords, memoized
  // reference) for the duration of the selection; it clears on deselect and
  // re-latches when a different place is selected.
  const latchedSelectedCenterRef = useRef(null);
  if (!viewPlaceId) {
    latchedSelectedCenterRef.current = null;
  } else if (
    selectedPlaceOverlayCenter &&
    latchedSelectedCenterRef.current?.id !== viewPlaceId
  ) {
    latchedSelectedCenterRef.current = {
      id: viewPlaceId,
      lat: selectedPlaceOverlayCenter.lat,
      lng: selectedPlaceOverlayCenter.lng,
    };
  }
  const latchedSelected = latchedSelectedCenterRef.current;
  const trackedLat =
    viewPlaceId && latchedSelected?.id === viewPlaceId
      ? latchedSelected.lat
      : null;
  const trackedLng =
    viewPlaceId && latchedSelected?.id === viewPlaceId
      ? latchedSelected.lng
      : null;
  const stableTrackedLocation = useMemo(
    () =>
      trackedLat != null && trackedLng != null
        ? { lat: trackedLat, lng: trackedLng }
        : null,
    [trackedLat, trackedLng]
  );

  // 이름표는 **지도가 멈출 때마다** 다시 받는다.
  //
  // localBounds/localZoom 은 syncCameraOnMove 가 꺼져 있으면(핀에 맞춰 화면을 맞추는 중)
  // 갱신이 멈춘다. 그러면 이름표가 옛 범위 그대로 남아, 넓게 축소해도 예전 자리 몇 개만
  // 보인다(2026-08-07 사장님 화면: 서울 전체인데 숙대 근처 2개만). 그래서 따로 둔다.
  const [chipCamera, setChipCamera] = useState(null);
  const { chips: areaChips, level: areaLevel, clickZoom: areaClickZoom } = useAreaChips({
    bounds: chipCamera?.bounds || localBounds,
    zoom: chipCamera?.zoom ?? localZoom,
  });

  // 칩이 사라지는 배율부터 작은 이름표가 대신 뜬다(모바일과 같은 규칙).
  const landmarkLabels = useAreaLandmarks({
    bounds: chipCamera?.bounds || localBounds,
    zoom: chipCamera?.zoom ?? localZoom,
    minZoom: AREA_CHIP_MAX_ZOOM,
  });

  // 전국 지하철 한 벌(노선·역·출구). 화면 범위로 묻던 것을 파일 한 번 받기로 바꿨다 —
  // 지도를 움직여도 요청이 생기지 않는다.
  const subwayNetwork = useSubwayNetwork();

  // 목록도 같은 것을 보여줘야 하므로 위로 알려 준다.
  useEffect(() => {
    onAreaChipsChange?.({ chips: areaChips, level: areaLevel });
  }, [areaChips, areaLevel, onAreaChipsChange]);

  const syncLocalCamera = useCallback((camera) => {
    if (!camera) return;

    setLocalBounds(camera.bounds);
    setLocalCenter(camera.center);
    setLocalZoom(camera.zoom);
  }, []);

  useEffect(() => {
    if (mapBounds) setLocalBounds(mapBounds);
  }, [mapBounds]);

  useEffect(() => {
    if (!viewPlaceId || typeof document === "undefined") {
      setCompactPopupPortalElement(null);
      return undefined;
    }

    const element = document.createElement("div");
    element.className = "hotple-selected-place-popup-marker pointer-events-auto";
    element.style.pointerEvents = "auto";
    element.style.zIndex = "30";
    element.style.willChange = "transform";
    element.style.contain = "layout paint style";
    setCompactPopupPortalElement(element);

    return () => {
      setCompactPopupPortalElement((current) =>
        current === element ? null : current
      );
    };
  }, [viewPlaceId]);

  useEffect(() => {
    // Center on the selected place exactly ONCE per selection. Key the guard on
    // viewPlaceId ALONE and reset the ref ONLY on deselect (viewPlaceId null) —
    // NOT when the focus center is momentarily null. Previously the guard also
    // compared the center lat/lng and reset the ref whenever selectedPlaceFocusCenter
    // flickered to null (which happens mid-drag/scroll while the map-pin refresh
    // rebuilds markers). After that flicker, the reappearing center re-triggered
    // setLocalCenter → the camera snapped BACK to the selected pin while the user
    // was panning/zooming. Focusing once per selection kills that snap-back;
    // dragging/scrolling with a card open now leaves the camera where the user put it.
    if (!viewPlaceId) {
      focusedViewPlaceIdRef.current = null;
      return;
    }
    if (focusedViewPlaceIdRef.current === viewPlaceId) return;
    if (!selectedPlaceFocusCenter) return; // wait for a center; keep the ref intact

    focusedViewPlaceIdRef.current = viewPlaceId;
    // Center on the selected place but KEEP the current zoom — do NOT slam it to
    // a fixed zoom (founder found scale changes dizzying).
    setLocalCenter(selectedPlaceFocusCenter);
  }, [
    selectedPlaceFocusCenter,
    viewPlaceId,
  ]);

  const scheduleMapPinRefresh = useCallback(
    (bounds) => {
      if (!refreshMapMarkersForBounds || !bounds || viewPlaceIdInList) return;

      if (mapPinRefreshTimerRef.current) {
        clearTimeout(mapPinRefreshTimerRef.current);
      }

      mapPinRefreshTimerRef.current = setTimeout(() => {
        refreshMapMarkersForBounds(bounds);
      }, MAP_PIN_REFRESH_DEBOUNCE_MS);
    },
    [refreshMapMarkersForBounds, viewPlaceIdInList]
  );

  const refreshInitialMapPins = useCallback(
    (camera) => {
      const nextCamera = {
        bounds: camera?.bounds || localBounds,
        center: camera?.center || localCenter,
        zoom: camera?.zoom || localZoom,
      };

      const shouldRefreshInitialMapPins =
        !initialMapPinRefreshRef.current &&
        nextCamera.bounds &&
        !viewPlaceId &&
        !viewPlaceIdInList &&
        !searchPlace &&
        (!searchQuery || searchQuery === "My Location");

      if (!shouldRefreshInitialMapPins) return false;

      initialMapPinRefreshRef.current = true;
      syncLocalCamera(nextCamera);
      setMapBounds(nextCamera.bounds);
      setMapCenter(nextCamera.center);
      setMapZoom(nextCamera.zoom);
      scheduleMapPinRefresh(nextCamera.bounds);
      return true;
    },
    [
      localBounds,
      localCenter,
      localZoom,
      scheduleMapPinRefresh,
      searchPlace,
      searchQuery,
      setMapBounds,
      setMapCenter,
      setMapZoom,
      syncLocalCamera,
      viewPlaceId,
      viewPlaceIdInList,
    ]
  );

  const onInitialCameraReady = useCallback(
    (camera) => {
      if (camera) {
        syncLocalCamera(camera);
        setMapBounds(camera.bounds);
      }
      refreshInitialMapPins(camera);
    },
    [refreshInitialMapPins, setMapBounds, syncLocalCamera]
  );

  const onMapIdle = useCallback(
    (camera) => {
      const nextCamera = {
        bounds: camera?.bounds || localBounds,
        center: camera?.center || localCenter,
        zoom: camera?.zoom || localZoom,
      };

      syncLocalCamera(nextCamera);
      if (nextCamera.bounds && Number.isFinite(nextCamera.zoom)) {
        setChipCamera({ bounds: nextCamera.bounds, zoom: nextCamera.zoom });
      }

      const initialPinsRefreshed = !camera?.isUserInteraction
        ? refreshInitialMapPins(nextCamera)
        : false;

      if (camera?.isUserInteraction || initialPinsRefreshed) {
        setMapBounds(nextCamera.bounds);
        setMapCenter(nextCamera.center);
        setMapZoom(nextCamera.zoom);
      }
    },
    [
      localBounds,
      localCenter,
      localZoom,
      refreshInitialMapPins,
      setMapBounds,
      setMapCenter,
      setMapZoom,
      syncLocalCamera,
    ]
  );

  const onUserMoveEnd = useCallback(
    (camera) => {
      const nextCamera = {
        bounds: camera?.bounds || localBounds,
        center: camera?.center || localCenter,
        zoom: camera?.zoom || localZoom,
      };

      syncLocalCamera(nextCamera);
      setMapBounds(nextCamera.bounds);
      setMapCenter(nextCamera.center);
      setMapZoom(nextCamera.zoom);
      scheduleMapPinRefresh(nextCamera.bounds);

      // Auto-refresh the LEFT list to match mobile: refetch on a real PAN (>120m)
      // or a meaningful ZOOM change in either direction (>0.8), debounced so only
      // the settled view refetches. onUserMoveEnd only fires on genuine user
      // gestures — programmatic recenters/jumps don't trigger it — so this never
      // fires on the initial load or on selection recenters. We also skip while a
      // place is selected (view-place / view-place-in-list) so it doesn't fight
      // the selection's collapsed list.
      const suppressAreaRefresh = Boolean(viewPlaceId || viewPlaceIdInList);
      if (!suppressAreaRefresh && onAreaAutoRefreshRef.current) {
        const nextCenter = nextCamera.center;
        const nextZoom = nextCamera.zoom;
        const centerMoved =
          nextCenter &&
          areaRefreshDistanceMeters(lastAreaFetchCenterRef.current, nextCenter) >
            AREA_AUTO_REFRESH_MIN_PAN_METERS;
        // 축소만 보던 것을 확대까지 본다 — 확대하면 핀은 늘어나는데 목록이 그대로였다.
        const zoomChanged =
          nextZoom != null &&
          lastAreaFetchZoomRef.current != null &&
          Math.abs(nextZoom - lastAreaFetchZoomRef.current) >
            AREA_AUTO_REFRESH_MIN_ZOOM_CHANGE;
        if (centerMoved || zoomChanged) {
          if (nextCenter) lastAreaFetchCenterRef.current = nextCenter;
          if (nextZoom != null) lastAreaFetchZoomRef.current = nextZoom;
          if (areaAutoRefreshTimerRef.current) {
            clearTimeout(areaAutoRefreshTimerRef.current);
          }
          areaAutoRefreshTimerRef.current = setTimeout(() => {
            onAreaAutoRefreshRef.current?.(nextCamera);
          }, AREA_AUTO_REFRESH_DEBOUNCE_MS);
        }
      }
    },
    [
      localBounds,
      localCenter,
      localZoom,
      setMapBounds,
      setMapCenter,
      setMapZoom,
      scheduleMapPinRefresh,
      syncLocalCamera,
      viewPlaceId,
      viewPlaceIdInList,
    ]
  );

  const handleMapMarkerClick = useCallback(
    (placeId) => {
      // Just select. The map centers/focuses the selected place itself (via
      // trackedLocation + a FIXED focus zoom), so we must NOT also slam the zoom
      // here — that extra forced zoom fought the map's focus + the place-load
      // zoom, making the scale jump inconsistently on every click.
      if (placeId) addQueryParam("view-place", placeId);
    },
    [addQueryParam]
  );

  const handleMarkerClick = useCallback(
    (marker, markerPosition) => {
      handleMapMarkerClick(getPlaceId(marker), markerPosition);
    },
    [handleMapMarkerClick]
  );

  const handleGoToCurrentLocationClick = useCallback(() => {
    if (viewPlaceId) closePlaceView();
    onGoToCurrentLocation?.();
  }, [closePlaceView, onGoToCurrentLocation, viewPlaceId]);

  useEffect(() => {
    if (!Array.isArray(mapMarkers)) return;

    setLocalMarkers(mapMarkers);
    if (mapMarkers?.length > 0 && mapMarkers?.length <= 20) {
      const targetMarker = searchPlace ? mapMarkers[0] : null;
      const targetMarkerLocation = getPlaceLatLng(targetMarker);
      if (
        targetMarkerLocation &&
        !isCoordinateWithinBounds(targetMarkerLocation, localBounds) &&
        (targetMarkerLocation.lat !== localCenter?.lat ||
          targetMarkerLocation.lng !== localCenter?.lng)
      ) {
        setLocalCenter(targetMarkerLocation);
      }
    }
  }, [
    localBounds,
    localCenter?.lat,
    localCenter?.lng,
    mapMarkers,
    searchPlace,
  ]);

  useEffect(() => {
    if (
      mapRedPinCenter?.lat &&
      mapRedPinCenter?.lng &&
      (mapRedPinCenter.lat !== localCenter?.lat ||
        mapRedPinCenter.lng !== localCenter?.lng)
    ) {
      setLocalCenter(mapRedPinCenter);
    }
  }, [localCenter?.lat, localCenter?.lng, mapRedPinCenter]);

  // Sync the local camera to the container's mapCenter/mapZoom ONLY when they
  // GENUINELY change (area search, current location, radius) — never on a bare
  // DESELECT. Previously these fired on the `viewPlaceId → null` transition and
  // reverted localCenter from the focused pin back to a stale center; that change
  // made the shared map ease the camera sideways (the "map shifts on X" bug).
  //
  // CRITICAL: advance the ref to the latest mapCenter/mapZoom on EVERY change,
  // INCLUDING while a place is selected. Dragging the map while a card is open
  // updates the container mapCenter (onUserMoveEnd → setMapCenter); if the ref
  // were only advanced when not viewing, that drag center would look "changed" on
  // the next X and snap the camera to it. By always tracking the latest value, a
  // bare deselect finds centerChanged === false → no setLocalCenter → the camera
  // stays exactly where it is (on the pin the user just selected). We still never
  // move the camera WHILE selected (early return after the ref update), so the
  // only time a real change applies is a deliberate recenter made when nothing is
  // selected.
  const lastSyncedMapCenterRef = useRef(mapCenter);
  const lastSyncedMapZoomRef = useRef(mapZoom);

  useEffect(() => {
    const centerChanged = lastSyncedMapCenterRef.current !== mapCenter;
    lastSyncedMapCenterRef.current = mapCenter; // always track latest
    if (viewPlaceId) return; // never move the camera while a place is selected
    if (mapCenter && centerChanged) setLocalCenter(mapCenter);
  }, [mapCenter, viewPlaceId]);

  useEffect(() => {
    const zoomChanged = lastSyncedMapZoomRef.current !== mapZoom;
    lastSyncedMapZoomRef.current = mapZoom; // always track latest
    if (viewPlaceId) return; // never move the camera while a place is selected
    if (mapZoom && zoomChanged) setLocalZoom(mapZoom);
  }, [mapZoom, viewPlaceId]);

  useEffect(() => {
    return () => {
      if (mapPinRefreshTimerRef.current) {
        clearTimeout(mapPinRefreshTimerRef.current);
      }
    };
  }, []);

  const mapDisplayCenter = localCenter || mapCenter;
  const mapDisplayZoom = localZoom || mapZoom || DEFAULT_PLACES_MAP_ZOOM;
  const shouldFitDisplayedMarkers =
    disableCameraAnimation && !clusterMarkers && displayedMarkers.length > 1;

  return (
    <div
      data-maplibre-place-map
      className={`h-full ${
        isLeftSectionExpanded ? "w-[calc(100%-400px)]" : "w-full"
      } relative overflow-hidden bg-zinc-100`}
    >
      <div className="absolute left-0 right-0 top-0 z-10 flex w-full flex-col">
        {searchQuery === "My Location" && (
          <PlacesMapsDistanceFilterTabs
            mapRadius={mapRadius}
            handleRadiusChange={(_rad) => {
              setMapRadius(_rad);
              completePlacesReset(false, false, false);
            }}
          />
        )}
        {!viewPlaceIdInList && (
          <div className="flex w-full items-center justify-between gap-2 px-1 pt-2">
            <div className="flex items-center gap-2">
              {isLoadingPlaces && (
                <div
                  className="flex items-center gap-2 rounded-full border border-hairline bg-white/90 px-4 py-2 text-nowrap text-sm font-medium tracking-tight text-ink shadow-sm backdrop-blur"
                  aria-live="polite"
                >
                  <Iconify
                    icon={"lineicons:reload"}
                    className="animate-spin !text-inherit"
                  />
                  {t("searching this area")}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={openPlaceAddModal}
              // 장소를 등록하는 행동 버튼이다. 앱의 행동색(핑크)을 쓴다 —
              // 남색은 어느 화면에도 없던 색이었다.
              className="flex items-center gap-2 rounded-full bg-red px-5 py-2 text-nowrap text-sm font-semibold tracking-tight text-white transition hover:brightness-110"
            >
              <Iconify
                icon={"fluent:add-12-regular"}
                className={"!text-inherit"}
              />
              {t("add_a_missing_place")}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setIsLeftSectionExpanded((prev) => !prev)}
        className="absolute left-0 top-1/2 z-10 rounded-r-lg border border-l-0 border-brownish_black/30 bg-gray-50 px-1 py-3 text-xs text-black/60 dark:bg-dark_bg_grey dark:text-white/60"
      >
        <Iconify
          icon={"uiw:left"}
          className={`!h-4 !w-4 !text-inherit ${
            !isLeftSectionExpanded && "rotate-180"
          } transition`}
        />
      </button>
      {onGoToCurrentLocation ? (
        <button
          type="button"
          aria-label={t("go to current location")}
          title={t("go to current location")}
          onClick={handleGoToCurrentLocationClick}
          disabled={isLocatingUser}
          className="absolute bottom-10 right-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-ink shadow-lg shadow-black/15 transition hover:bg-ink/[0.04] disabled:cursor-wait disabled:opacity-70 dark:border-white/10 dark:bg-custom_black dark:text-white dark:hover:bg-white/10"
          data-current-location-control
        >
          <Iconify
            icon={
              isLocatingUser
                ? "line-md:loading-twotone-loop"
                : "mdi:crosshairs-gps"
            }
            className={`!h-5 !w-5 !text-inherit ${
              isLocatingUser ? "animate-spin" : ""
            }`}
          />
        </button>
      ) : null}

      <MapLibreMap
        cameraJumpKey={mapCameraJumpKey}
        pmtilesUrl={MAPLIBRE_PM_TILES_URL}
        markers={displayedMarkers}
        mapCenter={mapDisplayCenter}
        mapZoom={mapDisplayZoom}
        cameraAnimationDuration={disableCameraAnimation ? 0 : 350}
        scrollZoom={scrollZoom}
        clusterMarkers={clusterMarkers}
        // 넓게 보면 핀을 뿌리는 대신 상권(역) 이름표를 쓴다.
        //
        // 왜: 서울 전체가 한 화면에 들어오면 개별 핀은 의미가 없다. 게다가 한 번에
        // 가져오는 개수(400)에 걸려 묶음 하나가 "400곳" 이라고 뜨는데, 그건 실제 개수가
        // 아니라 상한이다(화면을 조금만 옮겨도 396·399·400 으로 흔들렸다 — 2026-08-06).
        // 역 이름표의 숫자는 미리 세어 둔 진짜 개수다.
        //
        // 이 값들은 모바일 지도가 이미 쓰고 있었다. 데스크톱에만 빠져 있었다.
        areaLabels={areaChips}
        areaLabelZoomMax={AREA_CHIP_MAX_ZOOM}
        landmarkLabels={landmarkLabels}
        subwayNetwork={subwayNetwork}
        areaLabelClickZoom={areaClickZoom}
        selectedMarkerId={selectedMarkerId}
        trackedLocation={stableTrackedLocation}
        trackedLocationOverlayElement={
          stableTrackedLocation ? compactPopupPortalElement : null
        }
        focusTrackedLocationOnChange={
          Boolean(stableTrackedLocation) && !shouldFitDisplayedMarkers
        }
        trackedLocationFocusZoom={mapDisplayZoom}
        trackedLocationFocusPadding={COMPACT_POPUP_FOCUS_PADDING}
        fitBoundsToMarkers={shouldFitDisplayedMarkers}
        fitBoundsPadding={viewPlaceId ? 140 : 96}
        fitBoundsMaxZoom={HOTPLE_SELECTED_PLACE_ZOOM}
        fitBoundsRequestKey={viewPlaceId || viewPlaceIdInList || searchQuery}
        syncCameraOnMove={!shouldFitDisplayedMarkers}
        onCameraChange={syncLocalCamera}
        onInitialCameraReady={onInitialCameraReady}
        onIdle={onMapIdle}
        onUserMoveEnd={onUserMoveEnd}
        onMarkerClick={handleMarkerClick}
      />

      {viewPlaceId && compactPopupPortalElement
        ? createPortal(
            <CompactPlaceMapPopup
              onClose={closePlaceView}
              onOpenDetail={routeToPlaceDetail}
              place={selectedPlacePreview}
            />,
            compactPopupPortalElement,
            viewPlaceId
          )
        : null}

    </div>
  );
};

export default PlaceMapMaplibre;
